// Review sittings: how long clinicians actually spend reviewing AI-drafted
// text (analytics/README.md, report_review_session_* and
// rms_letter_review_session_*).
//
// The QBR's Section B number is reconstructed from paragraph-approval
// timestamp deltas with a 30-minute gap rule — structurally blind to time
// before the first approval and to reading without approving. These events
// measure the sitting itself. The measurement model, agreed before wiring:
//
//   * duration_s is ENGAGED time, not wall-clock: the clock accrues only while
//     the tab is visible, and a stretch longer than the idle threshold with no
//     interaction contributes nothing beyond the last interaction. The
//     threshold is the QBR's own 30 minutes, so the new measure and the
//     historical proxy stay on the same footing, and it is stamped on every
//     ended row (idle_threshold_s) so a future change is self-describing.
//   * A sitting is one continuous VISIBLE spell. Hiding the tab ends the
//     sitting rather than pausing it, because the emit layer's flush fires on
//     that same transition — ending there is what gets the ended event
//     delivered before a tab close can cancel it (emitEvent.js documents the
//     residual loss; v_review_sittings repairs what still slips through,
//     labelled end_reason 'timeout'). Returning to the tab starts a new
//     sitting under a fresh sitting_id; per-module review time is the SUM of
//     sittings, and the paired _started/_ended rows keep every sitting
//     reconstructable.
//   * paragraphs_approved / paragraphs_edited count the actions the analytics
//     layer successfully emitted during the sitting, so they reconcile against
//     the report_paragraph_* / rms_letter_paragraph_* rows — consistent with
//     the event stream rather than with Firestore, deliberately.
//   * end_reason 'completed' means the module reached its first full approval
//     during the sitting (the report audit bridge's is_first_occurrence), or
//     the RMS page's own finish action ran. 'timeout' is never emitted from
//     here: it is the warehouse's repair label.
//
// One engine, two surfaces: the assessment report pages are tracked from the
// route (ReviewSessionTracker — no edits to the sixteen RD_*.js pages), the
// RMS letter page through useReviewSession. Every entry point swallows its own
// errors; measuring review time must never break a review.

import { emitAnalyticsEvent, flushAnalyticsEvents } from './emitEvent';
import { MAX_STRING_LEN } from './eventRegistry';

export const IDLE_THRESHOLD_MS = 30 * 60 * 1000;
const IDLE_THRESHOLD_S = IDLE_THRESHOLD_MS / 1000;

// Interactions that count as activity. 'scroll' does not bubble, so it is
// bound with capture to catch scrolling containers as well as the page.
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'];

// What counts as "actively editing": focus inside the contenteditable
// paragraph field, which is the one mechanism every review page uses (the
// shared contentEditableUtils), and which only the paragraph currently being
// edited carries as ="true". Deliberately NOT any input or textarea - a notes
// box or search field is not editing AI text, and overcounting here would
// quietly inflate the editing share of every sitting.
const EDITING_SELECTOR = '[contenteditable="true"]';

const isEditingTarget = (target) =>
  Boolean(target && typeof target.closest === 'function' && target.closest(EDITING_SELECTOR));

const cap = (value) => String(value).slice(0, MAX_STRING_LEN);

// What distinguishes the two surfaces: event names, identity fields, and how a
// paragraph action or module approval is matched to the active sitting.
const KINDS = {
  report: {
    startedEvent: 'report_review_session_started',
    endedEvent: 'report_review_session_ended',
    identity: (meta) => ({
      patient_id: cap(meta.patientId),
      module: cap(meta.module),
    }),
    matches: (meta, ref) =>
      ref.patientId !== undefined &&
      String(ref.patientId) === String(meta.patientId) &&
      (ref.module === undefined || String(ref.module) === String(meta.module)),
  },
  rms_letter: {
    startedEvent: 'rms_letter_review_session_started',
    endedEvent: 'rms_letter_review_session_ended',
    identity: (meta) => ({
      patient_record_id: cap(meta.patientRecordId),
      letter_type: cap(meta.letterType),
      ...(meta.pathway ? { pathway: cap(meta.pathway) } : {}),
    }),
    matches: (meta, ref) =>
      ref.patientRecordId !== undefined &&
      String(ref.patientRecordId) === String(meta.patientRecordId) &&
      (ref.letterType === undefined || String(ref.letterType) === String(meta.letterType)),
  },
};

// `mounted` survives across sittings: it is the screen (set on mount, cleared
// on unmount), while `sitting` is the current visible spell on it.
let mounted = null;
let sitting = null;
let listenersBound = false;

const newSittingId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const isDocumentVisible = () =>
  typeof document === 'undefined' || document.visibilityState !== 'hidden';

/** Never let a measurement failure surface in a clinical page. */
function safely(fn) {
  try {
    return fn();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[analytics] review session dropped:', error);
    return undefined;
  }
}

function beginSitting(now) {
  if (!mounted || sitting || !isDocumentVisible()) return;
  sitting = {
    sittingId: newSittingId(),
    startedAtMs: now,
    // Engaged time is closed segments plus the one currently open; a segment
    // opens at start/resume and closes at idle detection or sitting end.
    engagedMs: 0,
    segmentStartMs: now,
    lastActivityMs: now,
    // Active editing: time focused inside an editable paragraph. everEdited
    // distinguishes "entered a field and spent no measurable time" (0) from
    // "never entered a field" (the field is omitted from the payload).
    editingMs: 0,
    editingStartMs: null,
    everEdited: false,
    approved: 0,
    edited: 0,
    completed: false,
  };
  const kind = KINDS[mounted.kind];
  emitAnalyticsEvent(kind.startedEvent, {
    ...kind.identity(mounted.meta),
    sitting_id: sitting.sittingId,
  });
}

// Close the open segment as of `now`. A tail longer than the idle threshold is
// the clinician gone, and contributes nothing beyond their last interaction —
// the same rule the QBR applies to inter-approval gaps.
//
// An open editing spell is truncated at the SAME boundary, so active_edit_s can
// never exceed duration_s: a clinician who left a paragraph focused and walked
// away accrues neither engaged nor editing time beyond their last interaction.
// `editingReopenAt` is set when the segment closes but the field is still
// focused (the idle-gap resume path): the editing spell continues from there.
function closeSegment(now, editingReopenAt = null) {
  const tail = now - sitting.lastActivityMs;
  const segmentEnd = tail > IDLE_THRESHOLD_MS ? sitting.lastActivityMs : now;
  sitting.engagedMs += Math.max(0, segmentEnd - sitting.segmentStartMs);
  if (sitting.editingStartMs !== null) {
    sitting.editingMs += Math.max(0, segmentEnd - sitting.editingStartMs);
    sitting.editingStartMs = editingReopenAt;
  }
}

function endSitting(reason, now) {
  if (!mounted || !sitting) return;
  closeSegment(now);
  const kind = KINDS[mounted.kind];
  const ended = sitting;
  sitting = null;
  emitAnalyticsEvent(kind.endedEvent, {
    ...kind.identity(mounted.meta),
    sitting_id: ended.sittingId,
    duration_s: Math.max(0, Math.round(ended.engagedMs / 1000)),
    wall_clock_s: Math.max(0, Math.round((now - ended.startedAtMs) / 1000)),
    idle_threshold_s: IDLE_THRESHOLD_S,
    // Absent when no edit field was ever focused - a different fact from 0.
    ...(ended.everEdited
      ? { active_edit_s: Math.max(0, Math.round(ended.editingMs / 1000)) }
      : {}),
    paragraphs_approved: ended.approved,
    paragraphs_edited: ended.edited,
    end_reason: ended.completed ? 'completed' : reason,
  });
}

function recordActivity(now) {
  if (!sitting) {
    // No live sitting but the screen is still mounted: the clinician is back
    // (a resume the visibility handler can miss in older browsers).
    beginSitting(now);
    return;
  }
  if (now - sitting.lastActivityMs > IDLE_THRESHOLD_MS) {
    // The gap was idle: close the segment at the last interaction and open a
    // fresh one now. Gaps within the threshold simply stay inside the segment.
    // A still-focused editing spell resumes with the new segment.
    closeSegment(now, sitting.editingStartMs !== null ? now : null);
    sitting.segmentStartMs = now;
  }
  sitting.lastActivityMs = now;
}

const onActivity = () => safely(() => {
  if (!mounted) return;
  recordActivity(Date.now());
});

// Focus into an editable paragraph opens an editing spell; focus leaving it
// closes the spell. Document-level delegation, so no review page is touched.
// Focusing is itself activity (it can begin a sitting after an idle spell).
const onFocusIn = (event) => safely(() => {
  if (!mounted || !isEditingTarget(event.target)) return;
  const now = Date.now();
  recordActivity(now);
  if (sitting && sitting.editingStartMs === null) {
    sitting.editingStartMs = now;
    sitting.everEdited = true;
  }
});

const onFocusOut = (event) => safely(() => {
  if (!mounted || !sitting || sitting.editingStartMs === null) return;
  if (!isEditingTarget(event.target)) return;
  const now = Date.now();
  recordActivity(now);
  // recordActivity can close the segment on the idle path, which already
  // banks and reopens the editing spell; close whatever spell remains open.
  if (sitting.editingStartMs !== null) {
    sitting.editingMs += Math.max(0, now - sitting.editingStartMs);
    sitting.editingStartMs = null;
  }
});

const onVisibilityChange = () => safely(() => {
  if (!mounted) return;
  if (document.visibilityState === 'hidden') {
    endSitting('hidden', Date.now());
    // The emit layer's own hidden-flush may already be in flight; calling
    // again is a guarded no-op at worst, and at best it is what carries this
    // sitting's ended event out before the tab goes away.
    flushAnalyticsEvents();
  } else {
    beginSitting(Date.now());
  }
});

function bindListenersOnce() {
  if (listenersBound || typeof document === 'undefined') return;
  listenersBound = true;
  document.addEventListener('visibilitychange', onVisibilityChange);
  ACTIVITY_EVENTS.forEach((name) => {
    document.addEventListener(name, onActivity, { capture: true, passive: true });
  });
  // focusin/focusout bubble (unlike focus/blur), so plain document listeners
  // see every editable paragraph on every page.
  document.addEventListener('focusin', onFocusIn);
  document.addEventListener('focusout', onFocusOut);
}

/**
 * A review screen mounted. Starts a sitting immediately if the tab is visible;
 * otherwise the sitting starts when it becomes visible. If another screen was
 * still tracked (a route change with no unmount cleanup in between), its
 * sitting is closed as navigated first.
 */
export function startReviewTracking(kindName, meta) {
  return safely(() => {
    if (!KINDS[kindName] || !meta) return;
    if (mounted) endSitting('navigated', Date.now());
    mounted = { kind: kindName, meta };
    bindListenersOnce();
    beginSitting(Date.now());
  });
}

/**
 * The review screen unmounted or changed identity. `completed` lets a page
 * whose own finish action ran say so (the RMS letter review); the assessment
 * pages never pass it because the report audit bridge already notes first
 * full approval through noteReviewModuleApproved.
 */
export function stopReviewTracking({ completed = false } = {}) {
  return safely(() => {
    if (!mounted) return;
    if (sitting && completed) sitting.completed = true;
    endSitting('navigated', Date.now());
    mounted = null;
  });
}

/**
 * A paragraph action successfully reached the event stream. Called by
 * reportEvents.js / rmsLetterEvents.js after their own emit succeeded, so the
 * sitting's counts reconcile with the paragraph rows by construction.
 * Also counts as activity: a Firestore write is proof the clinician is here.
 */
export function noteReviewParagraphActivity({ action, ...ref }) {
  return safely(() => {
    if (!mounted || !sitting) return;
    if (!KINDS[mounted.kind].matches(mounted.meta, ref)) return;
    if (action === 'approved') sitting.approved += 1;
    else if (action === 'edited') sitting.edited += 1;
    else return;
    recordActivity(Date.now());
  });
}

/**
 * The report audit bridge saw an 'approved' entry. is_first_occurrence is the
 * only signal that a module just reached full approval for the first time —
 * that is what makes the sitting's end read 'completed'.
 */
export function noteReviewModuleApproved({ patientId, module, isFirstOccurrence }) {
  return safely(() => {
    if (!mounted || !sitting || mounted.kind !== 'report') return;
    if (!isFirstOccurrence) return;
    if (!KINDS.report.matches(mounted.meta, { patientId, module })) return;
    sitting.completed = true;
  });
}

/** The RMS letter page's own finish action ran during this sitting. */
export function noteReviewCompleted() {
  return safely(() => {
    if (sitting) sitting.completed = true;
  });
}

// Test-only: clear tracking state between tests. Listeners stay bound (they
// no-op with nothing mounted), matching how emitEvent handles its own.
export function __resetReviewSessionForTests() {
  mounted = null;
  sitting = null;
}
